import { useSiteStore } from '@stores/site';
import { ComponentAnswer, PatientSubmissionDataTypes } from '@type';
import { formatDate, getDateNow } from '@utils';

import { useDataSourcesStore } from '../data-sources';
import { usePatientStore } from '../patient';
import {
  FIRST_COMP_INDEX,
  FIRST_COMP_PREVIOUS_INDEX,
  initialState,
  STORE_NAME,
} from './care-task-store.constant';
import {
  getActiveCareTaskForAnswering,
  getActiveComponentAnswer,
} from './care-task-store.selectors';
import {
  AnsweringNavigateNextComponent,
  AnsweringNavigatePreviousComponent,
  GetType,
  IsCareTaskExist,
  PrepareAdditionalComponentAnswering,
  PrepareAnswerObject,
  PrepareCareTaskForAnswering,
  RemoveCareTask,
  ResetAnswering,
  ResetCareTasks,
  SetTasks,
  SetType,
  StartAnswering,
  UpdateAnswersBasedOnJump,
  UpdateCareTask,
  UpdateComponentAnswer,
  UpdateLastSeenIndex,
} from './care-task-store.types';
import {
  formatNewTaskToBeAdded,
  getAnswerObjectFromComponent,
  getComponentAnswer,
  getLastAnsweredIndex,
  getProgressPercentage,
  getTaskKey,
  getTimelyUpdatedTask,
} from './care-task-store.utils';

export const findTask = (get: GetType) => {
  return (taskKey: string) => {
    const { careTasks } = get();

    return careTasks.find(careTask => taskKey === careTask.key);
  };
};

export const setTasks = (set: SetType, get: GetType): SetTasks => {
  return tasks => {
    const { findTask } = get();

    const caretasksModels = tasks.map(task => {
      const potentialExistingTask = findTask(getTaskKey(task));

      return potentialExistingTask
        ? getTimelyUpdatedTask(potentialExistingTask)
        : formatNewTaskToBeAdded(task);
    });

    set(
      {
        careTasks: caretasksModels,
      },
      false,
      `${STORE_NAME}-SET_CARE_TASKS`
    );
  };
};

export const updateCareTask = (set: SetType, get: GetType): UpdateCareTask => {
  return (key, { template, answer }) => {
    if (!get().isCareTaskExist(key)) return;

    set(
      {
        careTasks: get().careTasks.map(item =>
          item.key === key
            ? {
                ...item,
                template: { ...item.template, ...(template || {}) },
                answer: answer || item.answer,
              }
            : item
        ),
      },
      false,
      `${STORE_NAME}-UPDATE_CARE_TASK_OCCURRENCE`
    );
  };
};

export const removeCareTask = (set: SetType, get: GetType): RemoveCareTask => {
  return key => {
    set(
      {
        careTasks: get().careTasks.filter(item => item.key !== key),
      },
      false,
      `${STORE_NAME}-REMOVE_CARE_TASK_OCCURRENCE`
    );
  };
};

export const isCareTaskExist = (get: GetType): IsCareTaskExist => {
  return key => get().careTasks.some(item => item.key === key) || false;
};

export const resetCareTasks = (set: SetType): ResetCareTasks => {
  return () => set(initialState, false, `${STORE_NAME}-RESET_CARE_TASKS`);
};

export const startAnswering = (set: SetType, get: GetType): StartAnswering => {
  return (siteUuid, taskId) => {
    const currentCaretask = get().careTasks.find(
      item => item.siteUuid === siteUuid && item.template.id === taskId
    );

    if (!currentCaretask) return;
    const mainComponentsLength =
      (currentCaretask.template?.components?.length ?? 0) - 1;
    const shouldResetLastSeen =
      currentCaretask.lastSeenCompIndex > FIRST_COMP_INDEX &&
      currentCaretask.lastSeenCompIndex >= mainComponentsLength - 1 &&
      currentCaretask.template.additionalComponents;

    set(
      {
        taskAnswering: {
          ...initialState.taskAnswering,
          navigator: {
            current:
              currentCaretask.lastSeenCompIndex >= FIRST_COMP_INDEX &&
              !shouldResetLastSeen
                ? currentCaretask.lastSeenCompIndex
                : FIRST_COMP_INDEX,
            previous:
              currentCaretask.lastSeenCompIndex - 1 > FIRST_COMP_PREVIOUS_INDEX
                ? currentCaretask.lastSeenCompIndex - 1
                : FIRST_COMP_PREVIOUS_INDEX,
          },
          siteUuid,
          taskId,
        },
      },
      false,
      `${STORE_NAME}-START_ANSWERING`
    );
  };
};
export const resetAnswering = (set: SetType): ResetAnswering => {
  return () =>
    set(
      {
        taskAnswering: { ...initialState.taskAnswering },
      },
      false,
      `${STORE_NAME}-RESET_ANSWERING`
    );
};

export const prepareCareTaskForAnswering = (
  set: SetType,
  get: GetType
): PrepareCareTaskForAnswering => {
  return async () => {
    const {
      taskAnswering: { siteUuid },
      updateCareTask,
    } = get();

    if (!siteUuid)
      throw new Error('For care task preparation, siteUuid cannot be null!');

    const { getSourceGateway } = useDataSourcesStore.getState();
    const { getSiteByUUID } = useSiteStore.getState();

    const siteData = getSiteByUUID(siteUuid);
    const source = await getSourceGateway(siteData.sourceProjectId);

    const activeCareTask = getActiveCareTaskForAnswering(get());

    if (activeCareTask.template.components === undefined) {
      const taskDetails = await source.getTaskDetails(
        activeCareTask.template.id,
        siteUuid
      );

      const {
        careplan: { uuid, uuidType, patientId },
        decryptedTaskComponents: { careplanComponents, scoringScheme },
      } = taskDetails;

      const answersObject: ComponentAnswer[] =
        getAnswerObjectFromComponent(careplanComponents);

      updateCareTask(activeCareTask.key, {
        template: {
          ...activeCareTask.template,
          components: careplanComponents,
          scoringScheme,
          uuid,
          uuidType,
          patientId,
        },
        answer: answersObject,
      });
    }

    if (activeCareTask.template.additionalComponents !== undefined) {
      set(
        state => ({
          ...state,
          taskAnswering: {
            ...state.taskAnswering,
            isAnsweringMain: false,
          },
        }),
        false,
        `${STORE_NAME}-ANSWERING_NAVIGATE_PREVIOUS_COMPONENT`
      );
    }
  };
};

export const answeringNavigateNextComponent = (
  set: SetType,
  get: GetType
): AnsweringNavigateNextComponent => {
  return () => {
    const currentAnswer = getActiveComponentAnswer(get());
    if (currentAnswer) get().updateAnswersBasedOnJump(currentAnswer);

    const { siteUuid, taskId } = get().taskAnswering;
    const currentIndex =
      currentAnswer?.jumpTo ?? get().taskAnswering.navigator.current + 1;

    const careTask = get().careTasks.find(
      item => item.siteUuid === siteUuid && item.template.id === taskId
    );
    if (!careTask) return;

    const mainComponentsLength =
      (careTask.template?.components?.length ?? 0) - 1;
    const shouldResetLastSeen =
      careTask.lastSeenCompIndex > FIRST_COMP_INDEX &&
      careTask.lastSeenCompIndex >= mainComponentsLength - 1 &&
      careTask.template.additionalComponents;
    const lastIndex =
      currentIndex > FIRST_COMP_INDEX ? currentIndex : FIRST_COMP_INDEX;
    const lastSeenIndex =
      currentIndex > FIRST_COMP_INDEX && !shouldResetLastSeen
        ? currentIndex
        : lastIndex;

    get().updateLastSeenIndex(careTask.key, lastSeenIndex);

    set(
      {
        taskAnswering: {
          ...get().taskAnswering,
          navigator: {
            current: currentIndex,
            previous: get().taskAnswering.navigator.current,
          },
        },
      },
      false,
      `${STORE_NAME}-ANSWERING_NAVIGATE_NEXT_COMPONENT`
    );
  };
};

export const answeringNavigatePreviousComponent = (
  set: SetType
): AnsweringNavigatePreviousComponent => {
  return () =>
    set(
      state => {
        const activeCareTask = getActiveCareTaskForAnswering(state);

        const targetAnswerIndex: number | undefined =
          activeCareTask.answer?.findIndex(
            item => item.jumpTo === state.taskAnswering.navigator.current
          );

        const target: number =
          targetAnswerIndex !== undefined && targetAnswerIndex > -1
            ? targetAnswerIndex
            : state.taskAnswering.navigator.previous;

        return {
          ...state,
          taskAnswering: {
            ...state.taskAnswering,
            navigator: {
              current: target,
              previous: target - 1,
            },
          },
        };
      },
      false,
      `${STORE_NAME}-ANSWERING_NAVIGATE_PREVIOUS_COMPONENT`
    );
};

export const updateAnswersBasedOnJump = (
  set: SetType
): UpdateAnswersBasedOnJump => {
  return answerObject =>
    set(
      state => {
        const activeCareTask = getActiveCareTaskForAnswering(state);
        let newAnswers = activeCareTask.answer;
        if (activeCareTask.answer) {
          newAnswers = activeCareTask.answer.map(
            (answer: ComponentAnswer, index: number) => {
              if (
                index > state.taskAnswering.navigator.current &&
                answerObject.jumpTo &&
                index < answerObject.jumpTo
              )
                return {
                  ...answer,
                  answer: null,
                  isSkipped: true,
                  hasError: false,
                  isJumped: true,
                  jumpTo: undefined,
                  score: undefined,
                };

              return answer;
            }
          );
        }

        return {
          ...state,
          careTasks: state.careTasks.map(careTask =>
            careTask.key === activeCareTask.key &&
            careTask.siteId === activeCareTask.siteId
              ? { ...careTask, answer: [...(newAnswers || [])] }
              : { ...careTask }
          ),
        };
      },
      false,
      `${STORE_NAME}-UPDATE_ANSWERS_BASED_ON_JUMP`
    );
};
export const updateComponentAnswer = (
  set: SetType,
  get: GetType
): UpdateComponentAnswer => {
  return answerObject => {
    const activeCareTask = getActiveCareTaskForAnswering(get());
    const answerIndex: number | undefined = activeCareTask.answer?.findIndex(
      item =>
        item.careplanCareplanComponentMapperId ===
        answerObject.componentMapperId
    );
    if (answerIndex === undefined) return;

    const answers = activeCareTask.answer || [];
    const newAnswer: ComponentAnswer = {
      ...answers[answerIndex],
      ...answerObject,
    };
    answers[answerIndex] = newAnswer;

    const lastAnsweredIndex = getLastAnsweredIndex({
      answers,
      currentComponentIndex: answerIndex,
      currentComponentAnswer: newAnswer.answer,
      currentLastAnsweredIndex: activeCareTask.lastAnsweredIndex,
    });

    set(
      {
        careTasks: get().careTasks.map(item =>
          item.key === activeCareTask.key
            ? {
                ...item,
                answer: [...answers],
                lastAnsweredIndex: lastAnsweredIndex,
                progressPercentage: getProgressPercentage({
                  lastAnsweredIndex,
                  totalLength: answers.length,
                }),
              }
            : item
        ),
      },
      false,
      `${STORE_NAME}-UPDATE_CARE_TASK_COMPONENT_ANSWER`
    );
  };
};

export const prepareAnswerObject = (get: GetType): PrepareAnswerObject => {
  return () => {
    const patientInfo = usePatientStore.getState().patientInfo;

    const { template, answer } = getActiveCareTaskForAnswering(get());

    if (!template.patientId || !template.uuid || !template.uuidType) {
      throw new Error(
        'Required task data {patientId, uuid, uuidType} are missing'
      );
    }

    const answerCollection =
      answer === null ? [] : answer.map(getComponentAnswer);

    const isFileAttached = answerCollection.some(
      item => !!item.file?.directLink
    );

    return {
      answeredBy: null,
      hasFile: isFileAttached,
      type: PatientSubmissionDataTypes.Caretask,
      patientId: template.patientId,
      name: template.name,
      uuid: template.uuid,
      timestamp: formatDate(getDateNow()),
      userSessionId: patientInfo.sessionId!,
      careplanId: template.id,
      careplanSQLId: template.id,
      uuidType: template.uuidType,
      timeZoneOffset: -1 * new Date().getTimezoneOffset(),
      occurrenceId: template.validOccurrence.id,
      careplanAnswerCollection: answerCollection,
    };
  };
};

export const prepareAdditionalComponentAnswering = (
  set: SetType,
  get: GetType
): PrepareAdditionalComponentAnswering => {
  return components => {
    const activeCareTask = getActiveCareTaskForAnswering(get());

    const answersObject: ComponentAnswer[] =
      getAnswerObjectFromComponent(components);

    get().updateCareTask(activeCareTask.key, {
      template: {
        ...activeCareTask.template,
        additionalComponents: components,
      },
      answer: (activeCareTask.answer || []).concat(answersObject),
    });

    set(
      {
        taskAnswering: {
          ...get().taskAnswering,
          isAnsweringMain: false,
          navigator: { ...initialState.taskAnswering.navigator },
        },
      },
      false,
      `${STORE_NAME}-PREPARE_ADDITIONAL_COMPONENT_ANSWERING`
    );
  };
};

export const updateLastSeenIndex = (
  set: SetType,
  get: GetType
): UpdateLastSeenIndex => {
  return (key, index) => {
    if (!get().isCareTaskExist(key)) return;

    const careTasks = get().careTasks.map(item =>
      item.key === key
        ? {
            ...item,
            lastSeenCompIndex: index,
          }
        : item
    );
    set({ careTasks }, false, `${STORE_NAME}-UPDATE_LAST_SEEN_INDEX`);
  };
};
